import { printHTML } from '../utils/printHelper';
import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, CalendarBlank, Eye, CheckCircle, Clock, Trash, EnvelopeSimple, PaperPlaneTilt, Printer, ChatCircleDots } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';

const fmtEUR = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('sr-Latn', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
};

export default function VATMonthlyPage() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [createMonth, setCreateMonth] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({ stanje_kartice: '', obaveza_mjesec: '', iznos_pdv: '', status: '', dokumentacija_primljena: false, napomena: '' });
  
  // Filters
  const [filterPIB, setFilterPIB] = useState('');
  const [filterNaziv, setFilterNaziv] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterObradjivac, setFilterObradjivac] = useState('');

  // Client lookup (client_id -> telefon) for showing phone column.
  // Keyed by client_id (unique) not PIB, because PIB may be a placeholder like "---" shared by multiple clients.
  const [clientPhoneMap, setClientPhoneMap] = useState({});
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin' || user.role === 'super_admin';

  useEffect(() => {
    fetchReports();
    // Fetch clients once to build client_id -> telefon map (initial load)
    api.get('/clients').then(res => {
      const m = {};
      (res.data || []).forEach(c => { if (c.id) m[c.id] = c.telefon || ''; });
      setClientPhoneMap(m);
    }).catch(() => {});
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get('/vat-monthly/reports');
      setReports(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju tabela');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId, mjesec) => {
    if (!(await confirmDialog(`Da li ste sigurni da želite obrisati PDV tabelu za ${mjesec}? Svi podaci će biti trajno izgubljeni.`))) return;
    try {
      await api.delete(`/vat-monthly/${reportId}`);
      toast.success(`PDV tabela za ${mjesec} obrisana`);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri brisanju tabele');
    }
  };

  const handleDeleteEntry = async (entryId, naziv) => {
    if (!(await confirmDialog(`Da li želite da obrišete klijenta "${naziv}" iz ove PDV tabele?\n\nOva akcija je trajna.`))) return;
    try {
      await api.delete(`/pdv-monthly/entry/${entryId}`);
      toast.success('Stavka obrisana');
      if (selectedReport) {
        // Refetch entries for current report
        await handleViewDetails(selectedReport);
      }
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri brisanju stavke');
    }
  };

  const [sendingEmails, setSendingEmails] = useState(null);
  const [sendingViber, setSendingViber] = useState(null);

  const handleSendEmails = async (reportId, mjesec, onlyFailed = false) => {
    const confirmMsg = onlyFailed
      ? `Poslati PDV email obavještenja SAMO klijentima kojima ranije slanje nije uspjelo za ${mjesec}?\n\nSlanje će biti pokrenuto u pozadini sa pauzom od 25 sekundi između mejlova.`
      : `Poslati PDV email obavještenja SVIM klijentima za ${mjesec}?\n\nSlanje će biti pokrenuto u pozadini sa pauzom od 25 sekundi između mejlova (zbog SMTP limita). Možete pratiti progres u stranici 'Poslati emailovi'.`;
    if (!(await confirmDialog(confirmMsg))) return;
    setSendingEmails(reportId);
    try {
      const url = onlyFailed
        ? `/pdv-monthly/${reportId}/send-emails?only_failed=true`
        : `/pdv-monthly/${reportId}/send-emails`;
      const res = await api.post(url);
      const data = res.data;
      if (data.started) {
        toast.success(
          `📨 Slanje pokrenuto u pozadini! ${data.eligible} mejlova, ~${data.eta_minutes} min.` +
          (onlyFailed && data.already_done > 0 ? ` Preskočeno ${data.already_done} (već uspješno poslato).` : '') +
          (data.skipped > 0 ? ` Bez emaila: ${data.skipped}.` : '') +
          ` Pratite progres u 'Poslati emailovi'.`,
          { duration: 12000 }
        );
      } else {
        toast.info(data.message || `Nema mejlova za slanje.`, { duration: 8000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri slanju emailova');
    } finally {
      setSendingEmails(null);
    }
  };

  const handleSendViber = async (reportId, mjesec) => {
    if (!(await confirmDialog(`Poslati PDV Viber obavještenja svim klijentima za ${mjesec}?\n\nViber poruka će biti poslata samo klijentima koji imaju telefonski broj.`))) return;
    setSendingViber(reportId);
    try {
      const res = await api.post(`/pdv-monthly/${reportId}/send-viber`);
      const data = res.data;
      toast.success(`Viber: Poslato ${data.sent}, Neuspješno ${data.failed}, Preskočeno ${data.skipped}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri slanju Viber poruka');
    } finally {
      setSendingViber(null);
    }
  };

  const handleCreateReport = async () => {
    if (!createMonth) {
      toast.error('Unesite mjesec u formatu MM/YYYY');
      return;
    }

    try {
      const response = await api.post(`/vat-monthly/create?mjesec=${createMonth}`);
      toast.success(response.data.message);
      setIsCreateDialogOpen(false);
      setCreateMonth('');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri kreiranju tabele');
    }
  };

  // Refresh the client_id -> telefon map from latest client data
  const refreshClientPhoneMap = async () => {
    try {
      const res = await api.get('/clients');
      const m = {};
      (res.data || []).forEach(c => { if (c.id) m[c.id] = c.telefon || ''; });
      setClientPhoneMap(m);
      return m;
    } catch {
      return null;
    }
  };

  const handleViewDetails = async (report) => {
    setSelectedReport(report);
    // Reset filters
    setFilterPIB('');
    setFilterNaziv('');
    setFilterStatus('');
    setFilterObradjivac('');

    try {
      const [entriesRes, logsRes] = await Promise.all([
        api.get(`/vat-monthly/${report.id}/entries`),
        api.get(`/vat-monthly/${report.id}/notifications`),
        refreshClientPhoneMap(),
      ]);
      setEntries(entriesRes.data);
      setFilteredEntries(entriesRes.data);
      setNotificationLogs(logsRes.data);
      setIsDetailsDialogOpen(true);
    } catch (error) {
      toast.error('Greška pri učitavanju detalja');
    }
  };

  const applyFilters = async () => {
    if (!selectedReport) return;
    
    try {
      const params = {};
      if (filterPIB) params.pib = filterPIB;
      if (filterNaziv) params.naziv = filterNaziv;
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus === 'bez_statusa' ? '__bez_statusa__' : filterStatus;
      if (filterObradjivac) params.obradjivac = filterObradjivac;
      
      const response = await api.get(`/vat-monthly/${selectedReport.id}/entries`, { params });
      setFilteredEntries(response.data);
    } catch (error) {
      toast.error('Greška pri filtriranju');
    }
  };

  const clearFilters = () => {
    setFilterPIB('');
    setFilterNaziv('');
    setFilterStatus('');
    setFilterObradjivac('');
    setFilteredEntries(entries);
  };

  useEffect(() => {
    if (selectedReport) {
      applyFilters();
    }
  }, [filterPIB, filterNaziv, filterStatus, filterObradjivac]);

  const [sendingResultEmail, setSendingResultEmail] = useState(null);
  const [sendingEntryViber, setSendingEntryViber] = useState(null);
  const [sendingEntryEmail, setSendingEntryEmail] = useState(null);

  // Email/Viber se može poslati samo kada je status "Unijeto" (PDV prijava unijeta).
  // Defensive normalization: trim whitespace + case-insensitive matching tako
  // da spori unos ili migrirani podaci ne blokiraju slanje.
  const EMAIL_ALLOWED_STATUSES = ['Unijeto'];
  const isStatusAllowed = (s) => EMAIL_ALLOWED_STATUSES.some(
    (allowed) => allowed.toLowerCase() === String(s || '').trim().toLowerCase()
  );

  const handleSendResultEmail = async (entryId) => {
    // Find the entry to check status BEFORE sending
    const entry = entries.find(e => e.id === entryId);
    if (!entry) return;

    // Status guard: only allow sending after the PDV declaration is entered
    if (!isStatusAllowed(entry.status)) {
      toast.error('Email se može poslati samo kada je status "Unijeto". Prvo unesite PDV prijavu.');
      return;
    }

    setSendingResultEmail(entryId);
    // Show a long-lived loading toast — PDF (virman) generation via WeasyPrint
    // can take 5–15s on production, so the user sees what's happening.
    const loadingToastId = toast.loading(`Šaljem email za ${entry.naziv_firme}... (može trajati do 30s)`);
    try {
      const res = await api.post(`/pdv-monthly/entry/${entryId}/send-result-email`, null, { timeout: 60000 });
      toast.success(res.data.message, { id: loadingToastId });
      // Refresh entries to show email sent status
      if (selectedReport) {
        const entriesRes = await api.get(`/vat-monthly/${selectedReport.id}/entries`);
        setEntries(entriesRes.data);
        setFilteredEntries(entriesRes.data);
      }
    } catch (error) {
      console.error('send-result-email failed:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Nepoznata greška';
      toast.error(`Email greška: ${detail}`, { id: loadingToastId, duration: 10000 });
    } finally {
      setSendingResultEmail(null);
    }
  };

  const handleSendEntryViber = async (entryId) => {
    // Find entry and check status
    const entry = entries.find(e => e.id === entryId) || (editingEntry?.id === entryId ? editingEntry : null);
    if (entry && !isStatusAllowed(entry.status)) {
      toast.error('Viber se može poslati samo kada je status "Unijeto". Prvo unesite PDV prijavu.');
      return;
    }
    setSendingEntryViber(entryId);
    const loadingToastId = toast.loading(`Šaljem Viber za ${entry?.naziv_firme || ''}...`);
    try {
      const res = await api.post(`/pdv-monthly/entry/${entryId}/send-viber`, null, { timeout: 60000 });
      toast.success(res.data.message, { id: loadingToastId });
      if (selectedReport) {
        const entriesRes = await api.get(`/vat-monthly/${selectedReport.id}/entries`);
        setEntries(entriesRes.data);
        setFilteredEntries(entriesRes.data);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Nepoznata greška';
      toast.error(`Viber greška: ${detail}`, { id: loadingToastId, duration: 10000 });
    } finally {
      setSendingEntryViber(null);
    }
  };

  const handleSendEntryEmail = async (entryId) => {
    setSendingEntryEmail(entryId);
    try {
      const res = await api.post(`/pdv-monthly/entry/${entryId}/send-email-napomena`);
      toast.success(res.data.message);
      if (selectedReport) {
        const entriesRes = await api.get(`/vat-monthly/${selectedReport.id}/entries`);
        setEntries(entriesRes.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri slanju emaila');
    } finally {
      setSendingEntryEmail(null);
    }
  };


  const handlePrintNalog = (entryId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/pdv-monthly/entry/${entryId}/nalog-prenos`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) return res.json().then(d => { throw new Error(d.detail || 'Greška'); });
        return res.text();
      })
      .then(html => {
        printHTML(html);
      })
      .catch(err => toast.error(err.message));
  };

  const handleUpdateEntry = async (entryId, updateData) => {
    try {
      await api.put(`/vat-monthly/entry/${entryId}`, null, {
        params: updateData
      });
      toast.success('Stavka ažurirana');

      // Close the modal immediately so the user gets instant feedback even if
      // the background refresh below hiccups.
      setEditingEntry(null);

      // Refresh entries with current filters
      if (selectedReport) {
        try {
          const params = {};
          if (filterPIB) params.pib = filterPIB;
          if (filterNaziv) params.naziv = filterNaziv;
          if (filterStatus && filterStatus !== 'all') params.status = filterStatus === 'bez_statusa' ? '__bez_statusa__' : filterStatus;
          if (filterObradjivac) params.obradjivac = filterObradjivac;

          const response = await api.get(`/vat-monthly/${selectedReport.id}/entries`, { params });
          setFilteredEntries(response.data);

          // Also refresh full entries list
          const allEntries = await api.get(`/vat-monthly/${selectedReport.id}/entries`);
          setEntries(allEntries.data);
        } catch (refreshErr) {
          // Don't block modal close for refresh issues
          console.warn('Refresh entries failed:', refreshErr);
        }
      }
    } catch (error) {
      toast.error('Greška pri ažuriranju');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'zavrseno':
        return 'bg-green-600 text-white border-green-700';
      case 'u_obradi':
        return 'bg-blue-700 text-white border-blue-800';
      case 'unijeto':
        return 'bg-red-700 text-white border-red-800';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'primljeno': 'Primljeno',
      'u_obradi': 'U obradi',
      'zavrseno': 'Završeno',
      'unijeto': 'Unijeto'
    };
    return labels[status] || 'Bez statusa';
  };

  const handlePrintTable = (report) => {
    if (!report || !filteredEntries || filteredEntries.length === 0) {
      toast.error('Nema stavki za štampu');
      return;
    }
    const rows = filteredEntries.map(e => `
      <tr>
        <td>${e.redni_broj || ''}</td>
        <td>${(e.naziv_firme || '').replace(/</g, '&lt;')}</td>
        <td>${e.pib || ''}</td>
        <td>${(clientPhoneMap[e.client_id] || '-')}</td>
        <td class="num">${fmtEUR(e.iznos_pdv || 0)}</td>
        <td>${getStatusLabel(e.status)}</td>
        <td>${e.dokumentacija_primljena ? 'DA' : '-'}</td>
        <td>${(e.napomena || '').replace(/</g, '&lt;')}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>PDV Tabela ${report.mjesec}</title>
<style>
@page{size:A4 landscape;margin:10mm}
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10pt;color:#000;margin:0}
.hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px}
.hdr h1{font-size:14pt;margin:0}
.hdr .sub{font-size:9pt;color:#555}
table{width:100%;border-collapse:collapse;margin-top:6px}
th,td{border:1px solid #888;padding:4px 6px;text-align:left;font-size:9pt;vertical-align:top}
th{background:#e8eef7;font-weight:bold;text-transform:uppercase;font-size:8pt}
td.num{text-align:right;font-family:monospace}
tr:nth-child(even) td{background:#fafafa}
.footer{margin-top:10px;font-size:8pt;color:#666;display:flex;justify-content:space-between}
@media print{body{margin:0}}
</style></head><body>
<div class="hdr">
  <div><h1>PDV TABELA ${report.mjesec}</h1><div class="sub">Datum štampe: ${new Date().toLocaleString('sr-Latn-ME')}</div></div>
  <div class="sub">Ukupno stavki: <b>${filteredEntries.length}</b> / ${entries.length}</div>
</div>
<table>
  <thead><tr>
    <th style="width:32px">RB</th><th>Naziv</th><th style="width:90px">PIB</th><th style="width:100px">Telefon</th>
    <th style="width:90px">Iznos PDV</th><th style="width:90px">Status</th><th style="width:55px">Dok.</th><th>Zabilješka</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">
  <div>CRM FINANCE — Mjesečna PDV Tabela</div>
  <div>Stranica generisana: ${new Date().toLocaleDateString('sr-Latn-ME')}</div>
</div>
<script>window.onload=function(){window.print();window.onafterprint=function(){window.close()}}</script>
</body></html>`;
    printHTML(html);
  };

  return (
    <div className="space-y-6" data-testid="vat-monthly-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Mjesečne PDV Tabele</h3>
          <p className="text-sm text-slate-600 mt-1">Automatski generirane tabele PDV obveznika</p>
        </div>

        {isAdmin && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                data-testid="create-monthly-report-button"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Kreiraj tabelu
              </Button>
            </DialogTrigger>
          <DialogContent data-testid="create-report-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium text-slate-900">
                Kreiraj novu mjesečnu PDV tabelu
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Sistem će automatski povući sve aktivne PDV obveznike i poslati im obavještenja.
              </p>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                  Mjesec (format: MM/YYYY)
                </Label>
                <Input
                  placeholder="05/2026"
                  value={createMonth}
                  onChange={(e) => setCreateMonth(e.target.value)}
                  className="bg-white border-slate-300 text-sm"
                  data-testid="create-month-input"
                />
                <p className="text-xs text-slate-500">Primjer: 05/2026 za maj 2026</p>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  className="rounded-md px-4 py-2 font-medium"
                >
                  Odustani
                </Button>
                <Button
                  onClick={handleCreateReport}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                  data-testid="create-report-submit"
                >
                  Kreiraj
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Učitavanje...</p>
        </div>
      ) : reports.length === 0 ? (
        <Card className="p-8 text-center" data-testid="reports-empty-state">
          <CalendarBlank size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500 mb-2">Nema mjesečnih PDV tabela</p>
          <p className="text-xs text-slate-400">Kreirajte prvu tabelu klikom na dugme iznad</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
          {reports.map((report) => (
            <Card
              key={report.id}
              className="p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewDetails(report)}
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                    <CalendarBlank size={20} weight="duotone" className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">PDV {report.mjesec}</h4>
                    <p className="text-xs text-slate-500">
                      {formatDate(report.created_at)}
                    </p>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendEmails(report.id, report.mjesec, false);
                      }}
                      disabled={sendingEmails === report.id}
                      className="p-2 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                      title="Pošalji email SVIM klijentima"
                      data-testid={`send-emails-${report.id}`}
                    >
                      {sendingEmails === report.id ? (
                        <Clock size={16} className="text-green-500 animate-spin" />
                      ) : (
                        <EnvelopeSimple size={16} className="text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendEmails(report.id, report.mjesec, true);
                      }}
                      disabled={sendingEmails === report.id}
                      className="p-2 hover:bg-amber-100 rounded transition-colors disabled:opacity-50"
                      title="Pošalji SAMO neuspjele (oni kojima ranije nije uspjelo)"
                      data-testid={`send-failed-${report.id}`}
                    >
                      <EnvelopeSimple size={16} weight="duotone" className="text-amber-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendViber(report.id, report.mjesec);
                      }}
                      disabled={sendingViber === report.id}
                      className="p-2 hover:bg-purple-100 rounded transition-colors disabled:opacity-50"
                      title="Pošalji Viber klijentima"
                      data-testid={`send-viber-${report.id}`}
                    >
                      {sendingViber === report.id ? (
                        <Clock size={16} className="text-purple-500 animate-spin" />
                      ) : (
                        <ChatCircleDots size={16} className="text-purple-600" />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReport(report.id, report.mjesec);
                      }}
                      className="p-2 hover:bg-red-100 rounded transition-colors"
                      title="Obriši tabelu"
                      data-testid={`delete-report-${report.id}`}
                    >
                      <Trash size={16} className="text-red-500" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Ukupno klijenata:</span>
                  <span className="font-medium text-slate-900">{report.total_clients}</span>
                </div>
                <div className="border-t border-slate-100 pt-2 mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500 text-xs">Nezavršeno:</span>
                    <span className="font-medium text-xs text-red-600">{report.total_clients - (report.status_counts?.zavrseno || 0)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-xs">Bez statusa:</span>
                      <span className="font-medium text-xs text-slate-400">{report.status_counts?.[''] || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-xs">Primljeno:</span>
                      <span className="font-medium text-xs text-amber-600">{report.status_counts?.primljeno || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-xs">U obradi:</span>
                      <span className="font-medium text-xs text-blue-600">{report.status_counts?.u_obradi || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-xs">Unijeto:</span>
                      <span className="font-medium text-xs text-slate-700">{report.status_counts?.unijeto || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500 text-xs">Završeno:</span>
                      <span className="font-medium text-xs text-emerald-600">{report.status_counts?.zavrseno || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-4"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails(report);
                }}
                data-testid={`view-report-${report.id}`}
              >
                <Eye size={16} className="mr-2" />
                Prikaži detalje
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-y-auto" data-testid="details-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-slate-900">
              PDV Tabela {selectedReport?.mjesec}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">
                    Ukupno klijenata
                  </p>
                  <p className="text-2xl font-semibold text-slate-900">{selectedReport.total_clients}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">
                    Poslato obavještenja
                  </p>
                  <p className="text-2xl font-semibold text-emerald-600">{selectedReport.notifications_sent}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-1">
                    Kreirano
                  </p>
                  <p className="text-sm font-medium text-slate-900">
                    {formatDateTime(selectedReport.created_at)}
                  </p>
                </Card>
              </div>

              {/* Entries Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900">Stavke tabele</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintTable(selectedReport)}
                      className="text-xs"
                      data-testid="print-pdv-table-btn"
                    >
                      Štampaj tabelu
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      className="text-xs"
                    >
                      Poništi filtere
                    </Button>
                  </div>
                </div>
                
                {/* Filters */}
                <Card className="p-4 mb-4 bg-slate-50">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">PIB</Label>
                      <Input
                        placeholder="Pretraži po PIB-u"
                        value={filterPIB}
                        onChange={(e) => setFilterPIB(e.target.value)}
                        className="bg-white border-slate-300 text-sm h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Naziv</Label>
                      <Input
                        placeholder="Pretraži po nazivu"
                        value={filterNaziv}
                        onChange={(e) => setFilterNaziv(e.target.value)}
                        className="bg-white border-slate-300 text-sm h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Status</Label>
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="bg-white border-slate-300 h-9">
                          <SelectValue placeholder="Svi statusi" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Svi statusi</SelectItem>
                          <SelectItem value="bez_statusa">Bez statusa</SelectItem>
                          <SelectItem value="primljeno">Primljeno</SelectItem>
                          <SelectItem value="u_obradi">U obradi</SelectItem>
                          <SelectItem value="unijeto">Unijeto</SelectItem>
                          <SelectItem value="zavrseno">Završeno</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Korisnik</Label>
                      <Input
                        placeholder="Pretraži po korisniku"
                        value={filterObradjivac}
                        onChange={(e) => setFilterObradjivac(e.target.value)}
                        className="bg-white border-slate-300 text-sm h-9"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Prikazano: {filteredEntries.length} / {entries.length} stavki
                  </p>
                </Card>
                
                <div className="w-full overflow-hidden bg-white rounded-md border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-600" data-testid="entries-table">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">RB</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Naziv</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">PIB</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Telefon</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Iznos PDV</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Status</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Dokumentacija</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Zabilješka</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Korisnik</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Email</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Akcije</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries.map((entry) => (
                          <tr key={entry.id} className="border-b border-slate-200 hover:bg-slate-50">
                            <td className="px-4 py-3">{entry.redni_broj}</td>
                            <td className="px-4 py-3 font-medium">{entry.naziv_firme}</td>
                            <td className="px-4 py-3">{entry.pib}</td>
                            <td className="px-4 py-3 text-xs text-slate-600">{clientPhoneMap[entry.client_id] || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={entry.iznos_pdv > 0 ? 'font-semibold text-slate-900' : 'text-slate-400'}>
                                {fmtEUR(entry.iznos_pdv)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(entry.status)}`}>
                                {getStatusLabel(entry.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {entry.dokumentacija_primljena ? (
                                <CheckCircle size={20} weight="duotone" className="text-emerald-600" />
                              ) : (
                                <Clock size={20} weight="duotone" className="text-slate-400" />
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {entry.napomena ? (
                                <p className="text-xs text-slate-600 max-w-[200px] truncate" title={entry.napomena}>{entry.napomena}</p>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {entry.updated_by_name ? (
                                <div>
                                  <p className="text-xs font-medium text-slate-700">{entry.updated_by_name}</p>
                                  <p className="text-xs text-slate-400">
                                    {formatDateTime(entry.updated_at)}
                                  </p>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {entry.email_rezultat_poslat ? (
                                <div className="flex items-center gap-1.5" title={entry.email_rezultat_datum ? `Poslat: ${formatDateTime(entry.email_rezultat_datum)}` : 'Poslat'}>
                                  <CheckCircle size={18} weight="duotone" className="text-green-600" />
                                  <span className="text-xs text-green-700 font-medium">Poslat</span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditingEntry(entry);
                                    setEditForm({
                                      stanje_kartice: String(entry.stanje_kartice || 0).replace('.', ','),
                                      obaveza_mjesec: String(entry.obaveza_mjesec || 0).replace('.', ','),
                                      iznos_pdv: String(entry.iznos_pdv || 0).replace('.', ','),
                                      status: entry.status,
                                      dokumentacija_primljena: entry.dokumentacija_primljena,
                                      napomena: entry.napomena || ''
                                    });
                                  }}
                                >
                                  Uredi
                                </Button>
                                {isAdmin && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteEntry(entry.id, entry.naziv_firme)}
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    title="Obriši stavku iz tabele"
                                    data-testid={`delete-entry-${entry.id}`}
                                  >
                                    <Trash size={14} />
                                  </Button>
                                )}
                                {entry.status && entry.status !== '' && (
                                  entry.email_rezultat_poslat ? (
                                    <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-300 whitespace-nowrap" title={entry.email_rezultat_datum ? `Poslan: ${formatDateTime(entry.email_rezultat_datum)}` : 'Poslan'}>
                                      <EnvelopeSimple size={12} weight="bold" className="inline mr-1" />Email
                                    </span>
                                  ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={sendingResultEmail === entry.id || !isStatusAllowed(entry.status)}
                                    onClick={() => handleSendResultEmail(entry.id)}
                                    className={isStatusAllowed(entry.status)
                                      ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200 hover:text-green-800 whitespace-nowrap'
                                      : 'text-slate-400 border-slate-200 cursor-not-allowed opacity-50 whitespace-nowrap'}
                                    title={isStatusAllowed(entry.status)
                                      ? 'Pošalji email rezultat klijentu'
                                      : 'Email se može poslati tek kada status bude "Unijeto"'}
                                    data-testid={`send-email-${entry.id}`}
                                  >
                                    {sendingResultEmail === entry.id ? (
                                      <><Clock size={14} weight="bold" className="animate-spin mr-1" /> Šalje...</>
                                    ) : (
                                      <><EnvelopeSimple size={14} weight="bold" className="mr-1" /> Email</>
                                    )}
                                  </Button>
                                  )
                                )}
                                {entry.napomena && entry.napomena.trim() && (
                                  entry.viber_poslat ? (
                                    <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-300 whitespace-nowrap" title={entry.viber_datum ? `Viber poslan: ${formatDateTime(entry.viber_datum)}` : 'Viber poslan'}>
                                      <ChatCircleDots size={12} weight="bold" className="inline mr-1" />Viber
                                    </span>
                                  ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={sendingEntryViber === entry.id || !isStatusAllowed(entry.status)}
                                    onClick={() => handleSendEntryViber(entry.id)}
                                    className={isStatusAllowed(entry.status)
                                      ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200 hover:text-purple-800 whitespace-nowrap'
                                      : 'text-slate-400 border-slate-200 cursor-not-allowed opacity-50 whitespace-nowrap'}
                                    title={isStatusAllowed(entry.status)
                                      ? 'Pošalji Viber iz zabilješke'
                                      : 'Viber se može poslati tek kada status bude "Unijeto"'}
                                    data-testid={`send-viber-${entry.id}`}
                                  >
                                    {sendingEntryViber === entry.id ? (
                                      <><Clock size={14} weight="bold" className="animate-spin mr-1" /> Šalje...</>
                                    ) : (
                                      <><ChatCircleDots size={14} weight="bold" className="mr-1" /> Viber</>
                                    )}
                                  </Button>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

{/* Notification logs removed - available on Obavještenja page */}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="sm:max-w-[720px] w-[92vw] max-h-[90vh] overflow-y-auto" data-testid="edit-entry-dialog">
          <DialogHeader>
            <DialogTitle>Ažuriraj stavku</DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-sm font-medium text-slate-900">{editingEntry.naziv_firme}</p>
                <p className="text-xs text-slate-500">PIB: {editingEntry.pib}</p>
              </div>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                    I - Stanje sa finansijske kartice (EUR)
                  </Label>
                  <Input type="text" inputMode="decimal" value={editForm.stanje_kartice}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.,-]/g, '');
                      const sk = parseFloat(val.replace(',', '.')) || 0;
                      const om = parseFloat(String(editForm.obaveza_mjesec).replace(',', '.')) || 0;
                      const ukupno = (sk + om).toFixed(2).replace('.', ',');
                      setEditForm({ ...editForm, stanje_kartice: val, iznos_pdv: ukupno });
                    }}
                    className="bg-white border-slate-300 text-sm" placeholder="-650,00 (kredit) ili 650,00 (obaveza)"
                    data-testid="edit-stanje-kartice" />
                  <p className="text-xs text-slate-400">Negativan (-) = kredit, Pozitivan (+) = obaveza</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                    II - Obaveza tekuceg mjeseca (EUR)
                  </Label>
                  <Input type="text" inputMode="decimal" value={editForm.obaveza_mjesec}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.,-]/g, '');
                      const sk = parseFloat(String(editForm.stanje_kartice).replace(',', '.')) || 0;
                      const om = parseFloat(val.replace(',', '.')) || 0;
                      const ukupno = (sk + om).toFixed(2).replace('.', ',');
                      setEditForm({ ...editForm, obaveza_mjesec: val, iznos_pdv: ukupno });
                    }}
                    className="bg-white border-slate-300 text-sm" placeholder="650,00 (obaveza) ili -650,00 (kredit)"
                    data-testid="edit-obaveza-mjesec" />
                </div>
                <div className="p-3 bg-slate-100 rounded-lg border border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-semibold uppercase text-slate-500">Ukupno (I + II)</span>
                    <span className={`text-lg font-bold ${parseFloat(String(editForm.iznos_pdv).replace(',', '.')) > 0 ? 'text-red-600' : parseFloat(String(editForm.iznos_pdv).replace(',', '.')) < 0 ? 'text-green-600' : 'text-slate-600'}`}>
                      {editForm.iznos_pdv || '0,00'} EUR
                    </span>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => {
                    const sk = parseFloat(String(editForm.stanje_kartice).replace(',', '.')) || 0;
                    const om = parseFloat(String(editForm.obaveza_mjesec).replace(',', '.')) || 0;
                    const ukupno = sk + om;
                    const clientName = editingEntry.naziv_firme || '';
                    const mjesec = selectedReport?.mjesec || '';
                    const mjesecBroj = parseInt(selectedReport?.broj_mjeseca || 1);
                    const godina = parseInt(selectedReport?.godina || 2026);
                    let prevM = mjesecBroj - 1;
                    let prevY = godina;
                    if (prevM < 1) { prevM = 12; prevY--; }
                    let nextM = mjesecBroj + 1;
                    let nextY = godina;
                    if (nextM > 12) { nextM = 1; nextY++; }
                    const rok = `15.${String(nextM).padStart(2,'0')}.${nextY}`;
                    const prevPeriod = `${String(prevM).padStart(2,'0')}/${prevY}`;
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    const ime = user.name || 'FINANCE GROUP';
                    const fmtN = (n) => Math.abs(n).toLocaleString('sr-Latn', {minimumFractionDigits:2, maximumFractionDigits:2});
                    let txt = `Postovani, ${clientName},\n\nNakon obrade PDV prijave na osnovu Vase dostavljene dokumentacije za period ${mjesec}, obavjestavamo Vas o sljedecem stanju:\n\n`;
                    txt += `Stanje sa finansijske kartice Poreske uprave zakljucno sa ${prevPeriod}: ${fmtN(sk)} EUR (${sk < 0 ? 'kredit' : sk > 0 ? 'obaveza' : 'nula'})\n`;
                    txt += `Obracun za ${mjesec}: ${fmtN(om)} EUR (${om < 0 ? 'kredit' : om > 0 ? 'obaveza' : 'nula'})\n`;
                    if (ukupno < 0) {
                      txt += `Ukupan PDV kredit: ${fmtN(ukupno)} EUR\n\n`;
                      txt += `U navedenom periodu nemate obavezu za uplatu PDV-a. Ostvareni kredit u iznosu od ${fmtN(ukupno)} EUR prenosi se u naredni poreski period.\n`;
                    } else if (ukupno > 0) {
                      txt += `Ukupna obaveza za PDV: ${fmtN(ukupno)} EUR\n\n`;
                      txt += `Molimo Vas da izvrsenu obavezu u iznosu od ${fmtN(ukupno)} EUR uplatite najkasnije do ${rok}. U prilogu dostavljamo nalog za prenos.\n`;
                    } else {
                      txt += `Ukupno: 0,00 EUR\n\n`;
                      txt += `U navedenom periodu nemate obavezu za uplatu PDV-a.\n`;
                    }
                    txt += `\nSrdacan pozdrav,\n\n${ime}`;
                    setEditForm(prev => ({ ...prev, napomena: txt }));
                  }} className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap" data-testid="auto-zabiljeska-btn">
                    Generisi zabiljesku
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value === 'bez_statusa' ? '' : value })}
                >
                  <SelectTrigger className="bg-white border-slate-300" data-testid="edit-status-select">
                    <SelectValue placeholder="Bez statusa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bez_statusa">Bez statusa</SelectItem>
                    <SelectItem value="primljeno">Primljeno</SelectItem>
                    <SelectItem value="u_obradi">U obradi</SelectItem>
                    <SelectItem value="unijeto">Unijeto</SelectItem>
                    <SelectItem value="zavrseno">Završeno</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Zabilješka</Label>
                <textarea
                  value={editForm.napomena}
                  onChange={(e) => setEditForm({ ...editForm, napomena: e.target.value })}
                  className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[80px] resize-y"
                  placeholder="Zabilješka..."
                  data-testid="edit-napomena-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.dokumentacija_primljena}
                  onChange={(e) => setEditForm({ ...editForm, dokumentacija_primljena: e.target.checked })}
                  className="rounded border-slate-300"
                  data-testid="edit-dokumentacija-checkbox"
                />
                <label className="text-sm text-slate-700">Dokumentacija primljena</label>
              </div>
              
              {editingEntry.updated_by_name && (
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    Posljednja izmjena: <span className="font-medium text-slate-700">{editingEntry.updated_by_name}</span>
                  </p>
                  <p className="text-xs text-slate-400">
                    {formatDateTime(editingEntry.updated_at)}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setEditingEntry(null)}
                  className="rounded-md px-4 py-2 font-medium"
                  data-testid="edit-cancel-button"
                >
                  Odustani
                </Button>
                {/* SAMO JEDAN email button — koristi napomenu kao sadržaj (uklonjen
                    duplikat za result-email). Dugme se prikazuje samo kada postoji
                    napomena (zabilješka). */}
                {editForm.napomena && editForm.napomena.trim() ? (
                  editingEntry.email_napomena_poslat ? (
                    <span className="px-3 py-2 rounded-md text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">Email poslan</span>
                  ) : (
                  <Button
                    variant="outline"
                    disabled={sendingEntryEmail === editingEntry.id || !isStatusAllowed(editForm.status)}
                    onClick={async () => {
                      // Status guard
                      if (!isStatusAllowed(editForm.status)) {
                        // eslint-disable-next-line no-console
                        console.warn('[Email modal] Status not allowed:', JSON.stringify(editForm.status));
                        toast.error('Email se može poslati samo kada je status "Unijeto".');
                        return;
                      }
                      // First save the napomena, then send email
                      try {
                        const stanjeNum = parseFloat(String(editForm.stanje_kartice).replace(',', '.')) || 0;
                        const obavezaNum = parseFloat(String(editForm.obaveza_mjesec).replace(',', '.')) || 0;
                        const iznosNum = parseFloat(String(editForm.iznos_pdv).replace(',', '.')) || 0;
                        await api.patch(`/vat-monthly/entry/${editingEntry.id}`, {
                          napomena: editForm.napomena,
                          stanje_kartice: stanjeNum,
                          obaveza_mjesec: obavezaNum,
                          iznos_pdv: iznosNum,
                          status: editForm.status,
                          dokumentacija_primljena: editForm.dokumentacija_primljena,
                        });
                        handleSendEntryEmail(editingEntry.id);
                      } catch (err) {
                        toast.error('Greska pri cuvanju');
                      }
                    }}
                    className={isStatusAllowed(editForm.status)
                      ? 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200 hover:text-blue-800 rounded-md px-4 py-2 font-medium'
                      : 'text-slate-400 border-slate-200 rounded-md px-4 py-2 font-medium cursor-not-allowed opacity-50'}
                    title={isStatusAllowed(editForm.status)
                      ? 'Pošalji email klijentu'
                      : 'Email se može poslati tek kada status bude "Unijeto"'}
                    data-testid="edit-send-email-napomena-button"
                  >
                    {sendingEntryEmail === editingEntry.id ? (
                      <><Clock size={16} className="mr-2 animate-spin" /> Šalje se...</>
                    ) : (
                      <><EnvelopeSimple size={16} className="mr-2" /> Pošalji email</>
                    )}
                  </Button>
                  )
                ) : null}
                {editForm.napomena && editForm.napomena.trim() ? (
                  editingEntry.viber_poslat ? (
                    <span className="px-3 py-2 rounded-md text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200">Viber poslan</span>
                  ) : (
                  <Button
                    variant="outline"
                    disabled={sendingEntryViber === editingEntry.id || !isStatusAllowed(editForm.status)}
                    onClick={async () => {
                      if (!isStatusAllowed(editForm.status)) {
                        toast.error('Viber se može poslati samo kada je status "Unijeto".');
                        return;
                      }
                      // First save the napomena, then send viber
                      try {
                        const stanjeNum = parseFloat(String(editForm.stanje_kartice).replace(',', '.')) || 0;
                        const obavezaNum = parseFloat(String(editForm.obaveza_mjesec).replace(',', '.')) || 0;
                        const iznosNum = parseFloat(String(editForm.iznos_pdv).replace(',', '.')) || 0;
                        await api.patch(`/vat-monthly/entry/${editingEntry.id}`, {
                          napomena: editForm.napomena,
                          stanje_kartice: stanjeNum,
                          obaveza_mjesec: obavezaNum,
                          iznos_pdv: iznosNum,
                          status: editForm.status,
                          dokumentacija_primljena: editForm.dokumentacija_primljena,
                        });
                        handleSendEntryViber(editingEntry.id);
                      } catch (err) {
                        toast.error('Greska pri cuvanju');
                      }
                    }}
                    className={isStatusAllowed(editForm.status)
                      ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200 hover:text-purple-800 rounded-md px-4 py-2 font-medium'
                      : 'text-slate-400 border-slate-200 rounded-md px-4 py-2 font-medium cursor-not-allowed opacity-50'}
                    title={isStatusAllowed(editForm.status)
                      ? 'Pošalji Viber klijentu'
                      : 'Viber se može poslati tek kada status bude "Unijeto"'}
                    data-testid="edit-send-viber-button"
                  >
                    {sendingEntryViber === editingEntry.id ? (
                      <><Clock size={16} className="mr-2 animate-spin" /> Šalje se...</>
                    ) : (
                      <><ChatCircleDots size={16} className="mr-2" /> Pošalji Viber</>
                    )}
                  </Button>
                  )
                ) : (
                  <Button
                    variant="outline"
                    disabled
                    className="text-slate-400 border-slate-200 rounded-md px-4 py-2 font-medium opacity-50"
                    title="Unesite zabilješku pa pošaljite Viber"
                  >
                    <ChatCircleDots size={16} className="mr-2" /> Pošalji Viber
                  </Button>
                )}
                <Button
                  onClick={() => {
                    const updates = {};
                    const iznosNum = parseFloat(String(editForm.iznos_pdv).replace(',', '.')) || 0;
                    const stanjeNum = parseFloat(String(editForm.stanje_kartice).replace(',', '.')) || 0;
                    const obavezaNum = parseFloat(String(editForm.obaveza_mjesec).replace(',', '.')) || 0;
                    if (iznosNum !== editingEntry.iznos_pdv) updates.iznos_pdv = iznosNum;
                    if (stanjeNum !== (editingEntry.stanje_kartice || 0)) updates.stanje_kartice = stanjeNum;
                    if (obavezaNum !== (editingEntry.obaveza_mjesec || 0)) updates.obaveza_mjesec = obavezaNum;
                    if (editForm.status !== editingEntry.status) updates.status = editForm.status;
                    if (editForm.dokumentacija_primljena !== editingEntry.dokumentacija_primljena) updates.dokumentacija_primljena = editForm.dokumentacija_primljena;
                    if (editForm.napomena !== (editingEntry.napomena || '')) updates.napomena = editForm.napomena;
                    if (Object.keys(updates).length > 0) {
                      handleUpdateEntry(editingEntry.id, updates);
                    } else {
                      setEditingEntry(null);
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                  data-testid="edit-save-button"
                >
                  Sačuvaj
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
